import React from 'react';
import { useTranslation } from 'react-i18next';

import { CopyButton, Icon, Text } from '../../../../components';
import {
  Root,
  Label,
  InputRow,
  LeadingSlot,
  Input,
  TrailingSlot,
  EyeToggle,
  RegenerateLink,
} from '../FieldRow/FieldRow.styles';

export type PasswordFieldProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /** Regenerate: replace the value with a fresh generated password. Omitted, there is no Regenerate
   *  control. */
  onRegenerate?: () => void;
  /** Accessible name of a copy button for the value (translated). Omitted, there is none. */
  copyLabel?: string;
  disabled?: boolean;
};

/**
 * The password row in the create form's field grammar (see `FieldRow.styles`): the EYE TOGGLE in
 * the leading slot (the one interactive leading icon), the input MASKED BY DEFAULT (every password
 * field in Scenario C starts hidden), then, in the trailing slot when the caller offers them, Copy
 * right after the value and Regenerate at the far right (the row grammar the mailbox page's
 * credential block uses: the copy belongs to the value it copies). The eye is local
 * state: toggling it re-renders only this field, never resets the value, and never touches anything
 * else on the form.
 */
export const PasswordField = ({
  label,
  value,
  onChange,
  onRegenerate,
  copyLabel,
  disabled = false,
}: PasswordFieldProps): React.ReactElement => {
  const { t } = useTranslation();
  const [revealed, setRevealed] = React.useState(false);
  const onToggle = React.useCallback(() => setRevealed((was) => !was), []);
  return (
    <Root $disabled={disabled}>
      <Label text={label} tag="span" />
      <InputRow>
        <LeadingSlot>
          <EyeToggle
            type="button"
            aria-label={revealed ? t('Hide password') : t('Show password')}
            aria-pressed={revealed}
            onClick={onToggle}
            disabled={disabled}
          >
            <Icon name={revealed ? 'eye' : 'eye-off'} size={16} />
          </EyeToggle>
        </LeadingSlot>
        <Input
          type={revealed ? 'text' : 'password'}
          value={value}
          aria-label={t(label)}
          disabled={disabled}
          autoComplete="new-password"
          spellCheck={false}
          onChange={(event) => onChange(event.target.value)}
          data-testid="mailbox-password-input"
        />
        {onRegenerate || copyLabel ? (
          <TrailingSlot>
            {/* Copy first, right after the value it copies; Regenerate alone at the far right. */}
            {copyLabel && value ? <CopyButton value={value} label={copyLabel} /> : null}
            {onRegenerate ? (
              <RegenerateLink type="button" onClick={onRegenerate} disabled={disabled}>
                <Text text="Regenerate" tag="span" />
              </RegenerateLink>
            ) : null}
          </TrailingSlot>
        ) : null}
      </InputRow>
    </Root>
  );
};

export default PasswordField;
